import queryString from "query-string";
import { translate } from "src/services/translation.service";
import { IFilter, IFilterValue } from "src/types/filter";
import { ISort } from "src/types/sort";

import environment from "../../environment";
import i18n from "../../i18n";

export const hashToSort = (hash: string, availableSorts: ISort[]): ISort => {
  const { idx }: { idx?: string } = queryString.parse(hash);
  let index = 0;

  if (idx) {
    const tmp = availableSorts.findIndex(
      (el: ISort) => el.id !== "" && idx.endsWith(el.id)
    );
    if (tmp >= 0) {
      index = tmp;
    }
  }

  return availableSorts[index];
};

export const sortToIndexName = (selectedSort?: ISort): string => {
  const { language } = i18n;
  const algoliaIndex =
    selectedSort && selectedSort.id ? `_${selectedSort.id}` : "";

  return (
    environment.ALGOLIA.ENV +
    `_b2c_${language}_${language}_products${algoliaIndex}`
  );
};

const regDFR = /^dFR\[([a-zA-Z0-9_.-]+)\]\[([0-9]+)\]$/;

export const hashToFilterValue = (
  hash: string,
  availableFilters: IFilter[]
): IFilterValue[] => {
  const hashParams = queryString.parse(hash);
  const selected: IFilterValue[] = [];
  let realMin: number | null = null;
  let realMax: number | null = null;

  Object.keys(hashParams).forEach((el: string) => {
    // every filters starting with dFR:
    if (el.startsWith("dFR[")) {
      const [, filter = null] = el.match(regDFR) || [];
      const { values = [] } =
        availableFilters.find(({ id }: { id: string }) => filter === id) || {};
      const select =
        values.find(({ id }: { id: string }) => hashParams[el] === id) || null;
      if (select) {
        selected.push(select);
      }
    }
    // price filters:
    else if (el.startsWith("nR[price.EUR.default]")) {
      const { values = [] } =
        availableFilters.find(
          ({ id }: { id: string }) => "price.EUR.default" === id
        ) || {};

      const minMaxDef = values.reduce(
        (val: { min: number; max: number } | null, { id }: { id: string }) => {
          const x = parseFloat(id);

          return {
            max: val === null || x > val.max ? x : val.max,
            min: val === null || x < val.min ? x : val.min,
          };
        },
        null
      );

      if (minMaxDef) {
        let { min, max } = minMaxDef;
        if (realMin !== null && realMax !== null) {
          min = min > realMin ? min : realMin;
          max = max < realMax ? max : realMax;
        }

        if (el.startsWith("nR[price.EUR.default][>=]")) {
          const minUrl = parseFloat(hashParams[el] as string);
          min = minUrl > min && minUrl < max ? minUrl : min;
        }
        if (el.startsWith("nR[price.EUR.default][<=]")) {
          const maxUrl = parseFloat(hashParams[el] as string);
          max = maxUrl < max && maxUrl > min ? maxUrl : max;
        }

        realMin = min;
        realMax = max;
      }
    }
  });

  if (realMin !== null && realMax !== null) {
    selected.push({
      filterRealLabel: translate("searchMenu.filters.price.EUR.default"),
      filterLabel: "price.EUR.default",
      id: `${realMin} TO ${realMax}`,
      label: `${realMin} € - ${realMax} €`,
      occurences: 1,
    });
  }

  return selected;
};

export const filtersAndSortToHash = (
  filters: IFilterValue[] = [],
  sort?: ISort,
  urlHash: string = window.location.hash
): string => {
  const { q: sq } = queryString.parse(window.location.search);
  // tslint:disable-next-line: prefer-const
  let { q, idx, p, is_v } = queryString.parse(urlHash);

  if (!q && sq) {
    q = sq;
  }

  if (sort) {
    idx = sortToIndexName(sort);
  }

  const newHashParams = { q, idx, p, is_v };
  filters.forEach((filter: IFilterValue) => {
    if (filter.filterLabel === "price.EUR.default") {
      const [min, max] = filter.id.split(" TO ");
      newHashParams["nR[price.EUR.default][>=][0]"] = min;
      newHashParams["nR[price.EUR.default][<=][0]"] = max;
    } else if (typeof filter.filterLabel === "string") {
      let i: number;
      // tslint:disable-next-line: no-empty
      for (i = 0; newHashParams[`dFR[${filter.filterLabel}][${i}]`]; i++) {}
      newHashParams[`dFR[${filter.filterLabel}][${i}]`] = filter.id;
    }
  });

  const hash = Object.keys(newHashParams).reduce(
    (acc: string, key: string): string =>
      newHashParams[key]
        ? (acc +=
            (acc !== "" ? "&" : "") +
            encodeURIComponent(key) +
            "=" +
            encodeURIComponent(newHashParams[key]))
        : acc,
    ""
  );

  return hash;
};
